import { AlertCircle, CheckCircle2, ChevronRight, Clock, XCircle } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import type { EmailEvent, EventHeaders } from '@/api'
import { Skeleton } from '@/components/ui/skeleton'
import { formatRelativeDate } from '@/lib/date-utils'
import {
  classificationEventTitle,
  classificationHeldBack,
  classificationReasons,
  eventTone,
  type EventTone,
} from './run-display'

const tones: Record<EventTone, { icon: typeof CheckCircle2; className: string; label: string }> = {
  done: {
    icon: CheckCircle2,
    className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    label: 'Finished',
  },
  attention: {
    icon: AlertCircle,
    className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    label: 'Needs attention',
  },
  error: {
    icon: XCircle,
    className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    label: 'Problem',
  },
  working: {
    icon: Clock,
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    label: 'In progress',
  },
}

export function EventToneIcon({ tone, className = '' }: { tone: EventTone; className?: string }) {
  const { icon: Icon, className: colors, label } = tones[tone]
  return (
    <span
      className={`flex size-9 shrink-0 items-center justify-center rounded-full ${colors} ${className}`}
      title={label}
    >
      <Icon className="size-4" aria-hidden />
      <span className="sr-only">{label}</span>
    </span>
  )
}

// Sender and subject come from the mailbox after the list renders:
// undefined while loading, null when the mailbox can't be read right now,
// `deleted` when the mailbox no longer has the email.
export function EventEmailHeading({
  headers,
  className = '',
}: {
  headers: EventHeaders | null | undefined
  className?: string
}) {
  if (headers === undefined)
    return (
      <span className={`block space-y-1.5 py-0.5 ${className}`} aria-label="Loading email details">
        <Skeleton className="h-3.5 w-32" />
        <Skeleton className="h-3.5 w-56 max-w-full" />
      </span>
    )
  if (headers === null)
    return (
      <span className={`block text-muted-foreground ${className}`}>
        Email details unavailable
      </span>
    )
  if (headers.deleted)
    return (
      <span className={`block text-muted-foreground ${className}`}>
        Email deleted from the mailbox
      </span>
    )
  return (
    <span className={`block min-w-0 ${className}`}>
      <span className="block truncate font-semibold">{headers.from}</span>
      <span className="block truncate">{headers.subject || '(No subject)'}</span>
    </span>
  )
}

export function EventListItem({
  event,
  headers,
}: {
  event: EmailEvent
  headers: EventHeaders | null | undefined
}) {
  const classification = event.classification
  const title = classification
    ? classificationEventTitle(classification)
    : (event.agent_summary ?? event.error_message ?? 'Processing email')
  const heldBack = classification && classificationHeldBack(classification)
  const detail = heldBack
    ? heldBack
    : classification?.reason
      ? classificationReasons[classification.reason]
      : classification?.result?.error
        ? 'We couldn’t work out what to do with this email.'
        : null
  return (
    <Link
      to="/events/$eventId"
      params={{ eventId: event.uuid }}
      className="group flex items-start gap-3 px-1 py-3 transition-colors hover:bg-muted/50 sm:px-3"
    >
      <EventToneIcon tone={eventTone(event)} className="mt-0.5" />
      <div className="sentry-block ph-no-capture min-w-0 flex-1 text-sm">
        <div className="flex items-start gap-3">
          <EventEmailHeading headers={headers} className="flex-1" />
          <span className="shrink-0 pt-0.5 text-xs text-muted-foreground">
            {formatRelativeDate(event.received_at ?? event.created_at)}
          </span>
        </div>
        <p className="mt-1 text-muted-foreground">
          <span className="font-medium text-foreground">{title}</span>
          {detail && <> · {detail}</>}
        </p>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">
          {event.connected_account.display_name}
        </p>
      </div>
      <ChevronRight className="mt-2 size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
    </Link>
  )
}
